import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, KOTCHA_IMAGE, BATH_SALT_IMAGE } from "../lib/api";
import RevealText from "../components/RevealText";
import ScrollReveal from "../components/ScrollReveal";
import KorvJar from "../components/KorvJar";
import KorvNet from "../components/KorvNet";
import KorvBottle from "../components/KorvBottle";
import KorvCandle from "../components/KorvCandle";
import KorvVessel from "../components/KorvVessel";
import { BATH_SALT_SCENTS } from "../lib/bathSalts";

const KOTCHA_COLOURS = [
  { code: "ivory",    label: "Ivory",    hex: "#EBE4D2" },
  { code: "bone",     label: "Bone",     hex: "#D9CFC0" },
  { code: "stone",    label: "Stone",    hex: "#A89985" },
  { code: "plum",     label: "Plum",     hex: "#3A2530" },
  { code: "charcoal", label: "Charcoal", hex: "#2A2724" },
  { code: "black",    label: "Black",    hex: "#141212" },
];

export default function Rituals() {
  const [balms, setBalms] = useState([]);
  const [objects, setObjects] = useState([]);

  useEffect(() => {
    api.get("/rituals").then((r) => setBalms(r.data)).catch(() => {});
    api.get("/ritual-objects").then((r) => setObjects(r.data)).catch(() => {});
  }, []);

  const byChapter = (c) => objects.filter((o) => o.chapter === c).sort((a,b) => a.order - b.order);

  return (
    <div className="bg-ink text-bone">
      {/* ═══ HERO ═══ */}
      <section className="pt-40 md:pt-56 pb-24 md:pb-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-3">
              <p className="text-[10px] uppercase tracking-eyebrow text-bone/55">Volume II</p>
              <p className="mt-2 text-[11px] uppercase tracking-editorial text-bone/70">Deux Rituels</p>
            </div>
            <div className="col-span-12 md:col-span-9">
              <RevealText
                as="h1"
                className="font-serif-display text-[14vw] md:text-[9vw] leading-[0.9] tracking-[-0.02em] max-w-[18ch]"
                lines={[
                  "Deux",
                  <span key="i" className="italic font-serif-italic">Rituels</span>,
                ]}
              />
              <motion.div
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 1.4, delay: 0.5 }}
                className="mt-14 font-serif-display text-4xl md:text-6xl leading-[1.05] text-bone/90"
              >
                Bath. <span className="italic font-serif-italic text-bone/75">Body.</span> Soul.
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 1.4, delay: 0.8 }}
                className="mt-10 text-[16px] leading-[1.65] text-bone/70 max-w-[52ch]"
              >
                Three chapters of the private ritual — objects for the room, the skin, and the air. Composed slowly, in the same handwriting as the lip, and made to become part of a lasting daily gesture.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CHAPTER I — BATH */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <ChapterHeader roman="I" title="Bath" tagline="The first ritual." />

      {/* KOTCHA HERO PANEL */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <ScrollReveal>
            <Link to="/rituals/kotcha" data-testid="ritual-object-kotcha" className="group block">
              <div className="relative overflow-hidden aspect-[16/9] md:aspect-[16/8]" style={{ background: "#1a1512" }}>
                {/* Reference photography — the house KOTCHA image */}
                <img
                  src={KOTCHA_IMAGE}
                  alt="KOTCHA — the luxury African bath net"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.02]"
                />
                {/* Soft vignette for legibility of overlay type */}
                <div aria-hidden className="absolute inset-0"
                  style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 70%)" }} />
                <div aria-hidden className="absolute inset-x-0 bottom-0 h-[26%]"
                  style={{ background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.55))" }} />

                {/* Meta corners */}
                <div className="absolute left-6 top-6 md:left-10 md:top-10 max-w-[46%]">
                  <p className="text-[10px] uppercase tracking-editorial text-bone/70">Chapter I · Bath</p>
                  <h3 className="font-serif-display text-6xl md:text-8xl leading-[0.9] mt-4 text-bone">
                    KOTCHA<sup className="text-2xl md:text-4xl align-super">™</sup>
                  </h3>
                  <p className="text-[11px] uppercase tracking-editorial text-bone/70 mt-3">
                    /kot.tʃa/ · The Luxury African Bath Net
                  </p>
                  <p className="mt-6 text-[15px] leading-[1.55] text-bone/85 max-w-[36ch]">
                    A timeless African bathing tradition. Elevated.
                  </p>
                </div>
                <div className="absolute right-6 bottom-6 md:right-10 md:bottom-10 flex items-center gap-3 text-[11px] uppercase tracking-editorial text-bone/85 group-hover:text-bone transition-colors">
                  <span>Discover</span>
                  <span className="w-6 h-[1px] bg-bone/70" />
                </div>
              </div>
            </Link>

            {/* Colour swatches strip */}
            <div className="mt-10 grid grid-cols-6 gap-4 md:gap-8 max-w-[900px]">
              {KOTCHA_COLOURS.map((c) => (
                <div key={c.code} className="text-center">
                  <div className="mx-auto mb-3" style={{ background: c.hex, width: "100%", aspectRatio: "1/1", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)" }} />
                  <p className="text-[9px] md:text-[10px] uppercase tracking-editorial text-bone/60">{c.label}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Bath — supporting objects */}
      <ObjectRow items={byChapter("bath").filter(o => o.slug !== "kotcha")} />

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CHAPTER II — BODY */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <ChapterHeader roman="II" title="Body" tagline="The restorative ritual." />
      <ObjectRow items={byChapter("body")} />

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CHAPTER III — SOUL */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <ChapterHeader roman="III" title="Soul" tagline="The atmospheric ritual." />
      <ObjectRow items={byChapter("soul")} />

      {/* ═══ THE SIGNATURES — the fragrance line ═══ */}
      <section className="py-32 md:py-44 border-t border-bone/15">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <div>
              <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">The Signatures</p>
              <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] max-w-[20ch]">
                Three <span className="italic font-serif-italic">scents</span>, across the three chapters.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {balms.map((b, i) => (
              <ScrollReveal key={b.slug} delay={i * 0.08} className="col-span-12 md:col-span-4">
                <Link to={`/rituals/${b.slug}`} className="group block">
                  <div className="relative aspect-[3/4] overflow-hidden" style={{ background: shade(b.swatch_hex, -6) }}>
                    <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(55% 55% at 50% 45%, rgba(255,240,220,0.10), transparent 65%)" }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-[54%] group-hover:translate-y-[-4px] transition-transform duration-700">
                        <KorvJar color={b.swatch_hex} accent={b.accent_hex} variant="signature" sublabel={`${b.name.toUpperCase()} · 50 ML`} animate={false} />
                      </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between">
                      <div>
                        <p className="font-serif-display text-3xl md:text-4xl">{b.name}</p>
                        <p className="text-[11px] uppercase tracking-editorial text-bone/60 mt-2">{b.notes.join(" · ")}</p>
                      </div>
                      <span className="text-[11px] uppercase tracking-editorial text-bone/60">From $58</span>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ────────── Chapter header ────────── */
function ChapterHeader({ roman, title, tagline }) {
  return (
    <section className="py-24 md:py-40 border-t border-bone/15">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
        <ScrollReveal className="col-span-12 md:col-span-2">
          <p className="chapter-num text-plum text-4xl md:text-5xl" style={{ color: "#c9a67a" }}>{roman}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.06} className="col-span-12 md:col-span-6">
          <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">Chapter {roman}</p>
          <h2 className="font-serif-display text-6xl md:text-9xl leading-[0.9] tracking-[-0.02em]">
            {title}
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={0.12} className="col-span-12 md:col-span-4 md:pt-32">
          <p className="font-serif-italic italic text-2xl md:text-3xl text-bone/70 max-w-[26ch]">
            {tagline}
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ────────── Editorial product row ────────── */
function ObjectRow({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="pb-24 md:pb-32">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <ul className="grid grid-cols-12 gap-6 md:gap-8">
          {items.map((o, i) => (
            <ScrollReveal key={o.slug} delay={i * 0.06} className={rowSpan(i)}>
              <ObjectCard o={o} />
            </ScrollReveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

function rowSpan(i) {
  const layouts = [
    "col-span-12 md:col-span-6",
    "col-span-12 md:col-span-6",
    "col-span-12 md:col-span-4",
    "col-span-12 md:col-span-4",
    "col-span-12 md:col-span-4",
  ];
  return layouts[i % layouts.length];
}

function ObjectCard({ o }) {
  // Bath Salt uses a full-bleed reference photo — no tonal stage.
  const isSalt = o.kind === "salt";
  const isKotcha = o.kind === "kotcha";
  const bg = isSalt || isKotcha ? "#1a1512" : shade(o.hero_swatch_hex, -6);
  return (
    <Link to={`/rituals/${o.slug}`} data-testid={`ritual-object-${o.slug}`} className="group block">
      <div className="relative overflow-hidden aspect-[4/5]" style={{ background: bg }}>
        {!isSalt && (
          <>
            <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(55% 55% at 50% 45%, rgba(255,240,220,0.10), transparent 70%)" }} />
            <div aria-hidden className="absolute inset-x-0 bottom-0 h-[26%]" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.42))" }} />
          </>
        )}
        <div className={`absolute inset-0 ${isSalt ? "" : "flex items-center justify-center p-6 md:p-10"}`}>
          <ObjectVisual o={o} />
        </div>
        {/* Legibility scrim for photo-backed cards */}
        {isSalt && (
          <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.55) 100%)" }} />
        )}

        {/* meta */}
        <div className="absolute left-4 top-4 text-[10px] uppercase tracking-editorial text-bone/70">{o.kind.toUpperCase()}</div>
        <div className="absolute right-4 top-4 text-[10px] uppercase tracking-editorial text-bone/60">{o.chapter.toUpperCase()}</div>

        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between">
          <div>
            <h4 className="font-serif-display text-3xl md:text-4xl leading-none">{o.name}</h4>
            <p className="text-[11px] uppercase tracking-editorial text-bone/60 mt-2 max-w-[24ch]">{o.subtitle}</p>
          </div>
          <span className="text-[11px] uppercase tracking-editorial text-bone/60 whitespace-nowrap ml-4">From {o.price_from}</span>
        </div>
      </div>
    </Link>
  );
}

function ObjectVisual({ o }) {
  const scent = o.variants?.find((v) => v.code === "encens") ? "ENCENS" : (o.variants?.[0]?.label?.toUpperCase() || "");
  if (o.kind === "kotcha") {
    return <div className="h-full w-auto flex items-center justify-center"><KorvNet color={o.hero_swatch_hex} variant="hanging" animate={false} className="h-[90%] w-auto" /></div>;
  }
  if (o.kind === "cleanser" || o.kind === "serum") {
    return <KorvBottle color={o.hero_swatch_hex} accent={o.accent_hex} productLabel={o.name.toUpperCase()} notes={o.variants?.slice(0,3).map(v=>v.label)||[]} volume={o.variants?.[0]?.volume || ""} animate={false} className="h-full w-auto" />;
  }
  if (o.kind === "oil") {
    return <KorvBottle color={o.hero_swatch_hex} accent={o.accent_hex} productLabel={o.name.toUpperCase()} notes={o.variants?.slice(0,3).map(v=>v.label)||[]} volume={o.variants?.[0]?.volume || ""} animate={false} className="h-full w-auto" />;
  }
  if (o.kind === "cream") {
    return <div className="w-[62%]"><KorvJar color={o.hero_swatch_hex} accent={o.accent_hex} variant="signature" sublabel={o.name.toUpperCase()} animate={false} /></div>;
  }
  if (o.kind === "salt") {
    // Bath Salt — the house reference photograph.
    return (
      <img
        src={BATH_SALT_IMAGE}
        alt="Deux Rituels bath salt — ceramic vessels"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }
  if (o.kind === "soak") {
    return <div className="w-[62%]"><KorvJar color={o.hero_swatch_hex} accent={o.accent_hex} variant="signature" sublabel={o.name.toUpperCase()} animate={false} /></div>;
  }
  if (o.kind === "candle") {
    return <KorvCandle color={o.hero_swatch_hex} accent={o.accent_hex} scent={scent} animate={false} className="h-[90%] w-auto" />;
  }
  if (o.kind === "diffuser" || o.kind === "spray" || o.kind === "incense") {
    return <KorvBottle color={o.hero_swatch_hex} accent={o.accent_hex} productLabel={o.name.toUpperCase()} notes={o.variants?.slice(0,3).map(v=>v.label)||[]} volume={o.variants?.[0]?.volume || ""} animate={false} className="h-full w-auto" />;
  }
  if (o.kind === "towel") {
    return <TowelStack o={o} />;
  }
  if (o.kind === "stone") {
    return <MassageStone o={o} />;
  }
  return null;
}

function TowelStack({ o }) {
  const cols = o.variants || [];
  return (
    <svg viewBox="0 0 260 300" className="h-full w-auto" aria-hidden>
      <defs>
        <radialGradient id="tg" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(20,18,17,0.42)" />
          <stop offset="1" stopColor="rgba(20,18,17,0)" />
        </radialGradient>
      </defs>
      <ellipse cx="130" cy="290" rx="110" ry="8" fill="url(#tg)" />
      {cols.slice(0, 5).map((v, i) => {
        const y = 60 + i * 34;
        return (
          <g key={v.code}>
            <rect x="30" y={y} width="200" height="30" rx="2" fill={v.swatch_hex || "#D9CFC0"} />
            <rect x="30" y={y} width="200" height="4" fill="rgba(0,0,0,0.10)" />
            <rect x="30" y={y+26} width="200" height="4" fill="rgba(0,0,0,0.20)" />
            <text x="220" y={y + 20} textAnchor="end"
              fill={isDark(v.swatch_hex||"#D9CFC0") ? "rgba(237,230,216,0.8)" : "rgba(20,18,17,0.7)"}
              fontFamily="Fraunces, serif" fontWeight="500" fontSize="12" letterSpacing="-0.5">
              korv.
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MassageStone({ o }) {
  return (
    <svg viewBox="0 0 260 260" className="h-full w-auto" aria-hidden>
      <defs>
        <radialGradient id="sg" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="rgba(20,18,17,0.5)" />
          <stop offset="1" stopColor="rgba(20,18,17,0)" />
        </radialGradient>
        <radialGradient id="stoneLight" cx="35%" cy="30%" r="70%">
          <stop offset="0" stopColor="rgba(255,255,255,0.14)" />
          <stop offset="0.7" stopColor="rgba(0,0,0,0)" />
          <stop offset="1" stopColor="rgba(0,0,0,0.28)" />
        </radialGradient>
      </defs>
      <ellipse cx="130" cy="230" rx="90" ry="8" fill="url(#sg)" />
      <path d="M 60 130 C 40 90, 90 60, 130 62 C 175 64, 220 100, 210 150 C 202 195, 145 210, 110 200 C 70 190, 78 162, 60 130 Z"
        fill={o.hero_swatch_hex} />
      <path d="M 60 130 C 40 90, 90 60, 130 62 C 175 64, 220 100, 210 150 C 202 195, 145 210, 110 200 C 70 190, 78 162, 60 130 Z"
        fill="url(#stoneLight)" />
    </svg>
  );
}

function shade(hex, percent) {
  const c = hex.replace("#", "");
  let r = parseInt(c.slice(0, 2), 16);
  let g = parseInt(c.slice(2, 4), 16);
  let b = parseInt(c.slice(4, 6), 16);
  const f = percent / 100;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * f)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * f)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * f)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

function isDark(hex) {
  const c = (hex || "#000").replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
}
